import type {
  ApprovalAssignmentType,
  OrgChartAssignment,
  OrgChartEmployee,
  OrgChartOut,
  OrgChartTeam,
  OrgChartUnit,
  OrgUnitType,
} from '@lms/shared-types'

/** Ordering for assignment rows shown on the right-hand "Approves them"
 *  panel. Mirrors the resolver's tier order in
 *  services/api/src/domain/approvers.py (primary first for the user,
 *  even though the resolver runs temporary_cover before primary). */
const APPROVES_ORDER: Record<ApprovalAssignmentType, number> = {
  primary: 0,
  backup: 1,
  hod_oversight: 2,
  temporary_cover: 3,
}

export interface OrgChartIndex {
  employeesById: Map<string, OrgChartEmployee>
  employeesByTeam: Map<string, OrgChartEmployee[]>
  /** Team id → team display name. Built from the employees list so we
   *  don't depend on a separate teams fetch. */
  teamNamesById: Map<string, string>
  /** Pre-sorted by team name, then employee name. */
  teamsOrdered: { id: string; name: string }[]
  primaryApproverByEmployee: Record<string, string | null>
  directReportsByApprover: Record<string, string[]>
  assignments: OrgChartAssignment[]
}

/** Build the cross-cutting indexes once per page load so the tree
 *  panel + detail panel render in O(employees) without rebuilding
 *  state from the wire payload on every keystroke. */
export function buildOrgChartIndex(data: OrgChartOut): OrgChartIndex {
  const employeesById = new Map<string, OrgChartEmployee>()
  const employeesByTeam = new Map<string, OrgChartEmployee[]>()
  const teamNamesById = new Map<string, string>()
  for (const emp of data.employees) {
    employeesById.set(emp.id, emp)
    teamNamesById.set(emp.teamId, emp.teamName)
    const bucket = employeesByTeam.get(emp.teamId) ?? []
    bucket.push(emp)
    employeesByTeam.set(emp.teamId, bucket)
  }
  for (const bucket of employeesByTeam.values()) {
    bucket.sort((a, b) => a.name.localeCompare(b.name))
  }
  const teamsOrdered = [...teamNamesById.entries()]
    .map(([id, name]) => ({ id, name }))
    .sort((a, b) => a.name.localeCompare(b.name))

  return {
    employeesById,
    employeesByTeam,
    teamNamesById,
    teamsOrdered,
    primaryApproverByEmployee: data.primaryApproverByEmployee,
    directReportsByApprover: data.directReportsByApprover,
    assignments: data.assignments,
  }
}

/** Case-insensitive substring match against name + email. Returns true
 *  for an empty query so the filter is opt-in. */
export function employeeMatchesQuery(
  emp: OrgChartEmployee,
  query: string,
): boolean {
  const q = query.trim().toLowerCase()
  if (q.length === 0) return true
  return (
    emp.name.toLowerCase().includes(q) ||
    emp.email.toLowerCase().includes(q)
  )
}

/** Returns the assignment rows where the selected employee is the SCOPE —
 *  i.e. these approvers act on this employee. Includes both
 *  ``scope_type='employee'`` rows targeted at the id and
 *  ``scope_type='team'`` rows whose ``scope_team_id`` matches the
 *  employee's team. Sorted by APPROVES_ORDER then approver name. */
export function approversFor(
  index: OrgChartIndex,
  employee: OrgChartEmployee,
): OrgChartAssignment[] {
  const rows: OrgChartAssignment[] = []
  for (const a of index.assignments) {
    const employeeScoped =
      a.scopeType === 'employee' && a.scopeEmployeeId === employee.id
    const teamScoped =
      a.scopeType === 'team' && a.scopeTeamId === employee.teamId
    if (employeeScoped || teamScoped) {
      rows.push(a)
    }
  }
  rows.sort((a, b) => {
    const tier = APPROVES_ORDER[a.assignmentType] - APPROVES_ORDER[b.assignmentType]
    if (tier !== 0) return tier
    return a.approverName.localeCompare(b.approverName)
  })
  return rows
}

/** Returns employees whose standing PRIMARY approver is the selected
 *  employee. Plus, when the selected employee has any
 *  ``hod_oversight`` assignment, every member of those teams (minus
 *  the selected employee themselves). Sorted by name. */
export function reportsFor(
  index: OrgChartIndex,
  employee: OrgChartEmployee,
): OrgChartEmployee[] {
  const directIds = index.directReportsByApprover[employee.id] ?? []
  const seen = new Set<string>(directIds)
  const out: OrgChartEmployee[] = []
  for (const id of directIds) {
    const emp = index.employeesById.get(id)
    if (emp) out.push(emp)
  }
  // HOD oversight: every team where this employee holds a non-revoked
  // hod_oversight assignment contributes its members.
  for (const a of index.assignments) {
    if (
      a.scopeType === 'team' &&
      a.assignmentType === 'hod_oversight' &&
      a.approverId === employee.id &&
      a.scopeTeamId != null
    ) {
      const members = index.employeesByTeam.get(a.scopeTeamId) ?? []
      for (const m of members) {
        if (m.id === employee.id || seen.has(m.id)) continue
        seen.add(m.id)
        out.push(m)
      }
    }
  }
  out.sort((a, b) => a.name.localeCompare(b.name))
  return out
}

export const ASSIGNMENT_TYPE_LABEL: Record<ApprovalAssignmentType, string> = {
  primary: 'Primary',
  backup: 'Backup',
  hod_oversight: 'HOD oversight',
  temporary_cover: 'Temporary cover',
}

// ── Structural org tree (org_units → teams → people) ─────────────────────────
//
// Separate from the approval-graph indexes above. This is the STRUCTURAL
// hierarchy the /hr/org-chart tree view renders: employee → team →
// parent-unit chain → root company. Pure —
// `buildOrgTree` takes the wire payload and returns nested nodes, so it is
// trivially unit-testable without a DOM.

/** A team node = leaf of the tree, with its members attached. */
export interface OrgTreeTeamNode {
  id: string
  name: string
  managerId: string | null
  parentUnitId: string | null
  members: OrgChartEmployee[]
}

/** A unit node. `childUnits` + `teams` are both sorted by name. */
export interface OrgTreeUnitNode {
  id: string
  name: string
  unitType: OrgUnitType
  parentId: string | null
  head: OrgChartEmployee | null
  childUnits: OrgTreeUnitNode[]
  teams: OrgTreeTeamNode[]
}

export interface OrgTree {
  /** Normally a single company root; kept as a list so a malformed graph
   *  (e.g. a unit whose parent is missing) still renders rather than
   *  silently vanishing. */
  roots: OrgTreeUnitNode[]
  /** Teams with no `parentUnitId`, or one pointing at a missing unit.
   *  Surfaced as "Unassigned" so placement gaps are visible, not hidden. */
  orphanTeams: OrgTreeTeamNode[]
}

function buildTeamNode(
  team: OrgChartTeam,
  employeesByTeam: Map<string, OrgChartEmployee[]>,
): OrgTreeTeamNode {
  const members = [...(employeesByTeam.get(team.id) ?? [])].sort((a, b) =>
    a.name.localeCompare(b.name),
  )
  return {
    id: team.id,
    name: team.name,
    managerId: team.managerId,
    parentUnitId: team.parentUnitId,
    members,
  }
}

/**
 * Assemble the structural tree from the org-chart payload. Units nest via
 * `parentId`; teams attach under `parentUnitId`; employees attach under
 * their `teamId`. A `visited` guard makes the walk cycle-safe even though
 * the DB blocks self-parenting — a defensive belt so the FE can never hang
 * on a malformed graph.
 */
export function buildOrgTree(data: OrgChartOut): OrgTree {
  // Defensive defaults: `orgUnits`/`teams` are recent additions to the
  // payload. Tolerate their absence (e.g. an older API build mid-deploy)
  // so a missing field degrades to an empty tree, never a page crash.
  const orgUnits = data.orgUnits ?? []
  const teams = data.teams ?? []
  const allEmployees = data.employees ?? []

  const employeesByTeam = new Map<string, OrgChartEmployee[]>()
  for (const emp of allEmployees) {
    const bucket = employeesByTeam.get(emp.teamId) ?? []
    bucket.push(emp)
    employeesByTeam.set(emp.teamId, bucket)
  }
  const employeesById = new Map<string, OrgChartEmployee>(
    allEmployees.map((e) => [e.id, e]),
  )

  const unitsById = new Map<string, OrgChartUnit>(
    orgUnits.map((u) => [u.id, u]),
  )
  const childUnitsByParent = new Map<string, OrgChartUnit[]>()
  const roots: OrgChartUnit[] = []
  for (const unit of orgUnits) {
    if (unit.parentId != null && unitsById.has(unit.parentId)) {
      const bucket = childUnitsByParent.get(unit.parentId) ?? []
      bucket.push(unit)
      childUnitsByParent.set(unit.parentId, bucket)
    } else {
      // Root (parentId null) or a dangling parent → treat as a root so it
      // still shows up.
      roots.push(unit)
    }
  }

  const teamsByParentUnit = new Map<string, OrgChartTeam[]>()
  const orphanTeams: OrgTreeTeamNode[] = []
  for (const team of teams) {
    if (team.parentUnitId != null && unitsById.has(team.parentUnitId)) {
      const bucket = teamsByParentUnit.get(team.parentUnitId) ?? []
      bucket.push(team)
      teamsByParentUnit.set(team.parentUnitId, bucket)
    } else {
      orphanTeams.push(buildTeamNode(team, employeesByTeam))
    }
  }

  const visited = new Set<string>()
  function buildUnitNode(unit: OrgChartUnit): OrgTreeUnitNode {
    visited.add(unit.id)
    const childUnits = [...(childUnitsByParent.get(unit.id) ?? [])]
      .filter((c) => !visited.has(c.id))
      .sort((a, b) => a.name.localeCompare(b.name))
      .map(buildUnitNode)
    const teams = [...(teamsByParentUnit.get(unit.id) ?? [])]
      .sort((a, b) => a.name.localeCompare(b.name))
      .map((t) => buildTeamNode(t, employeesByTeam))
    return {
      id: unit.id,
      name: unit.name,
      unitType: unit.unitType,
      parentId: unit.parentId,
      head: unit.headEmployeeId
        ? (employeesById.get(unit.headEmployeeId) ?? null)
        : null,
      childUnits,
      teams,
    }
  }

  return {
    roots: roots
      .sort((a, b) => a.name.localeCompare(b.name))
      .map(buildUnitNode),
    orphanTeams: orphanTeams.sort((a, b) => a.name.localeCompare(b.name)),
  }
}
